/**
 * ApplyProgressStep — 3-wave apply progress UI.
 * Shows per-wave status (pending/running/done/error).
 * FR-039, FR-040
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import type { WaveApplyResult } from '@roll1/shared';

interface WaveStatus {
  wave: number;
  label: string;
  status: 'pending' | 'running' | 'done' | 'error';
  result?: WaveApplyResult;
}

interface ApplyProgressStepProps {
  waves: WaveStatus[];
  onStop?: () => void;
  onCancel: () => void;
  isRunning: boolean;
}

/* [STYLE_TODO] Wave status icons and animations */

const STATUS_ICONS: Record<string, string> = {
  pending: '○',
  running: '◌',
  done: '●',
  error: '✕',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'text-muted-foreground',
  running: 'text-primary',
  done: 'text-ok',
  error: 'text-destructive',
};

export function ApplyProgressStep({ waves, onStop, onCancel, isRunning }: ApplyProgressStepProps) {
  return (
    <div className="space-y-4">
      <p className="text-ui font-medium">Применение импорта</p>

      <div className="space-y-3">
        {waves.map((wave) => (
          <div key={wave.wave} className="flex items-start gap-3 p-3 border rounded">
            <span className={`text-title ${STATUS_COLORS[wave.status]}`}>
              {STATUS_ICONS[wave.status]}
            </span>
            <div className="flex-1">
              <p className="text-ui font-medium">{wave.label}</p>
              {wave.status === 'running' && (
                <p className="text-meta text-muted-foreground animate-pulse">В процессе...</p>
              )}
              {wave.result && (
                <div className="text-meta mt-1">
                  <span className="text-ok">Создано: {wave.result.applied}</span>
                  {wave.result.failed > 0 && (
                    <span className="text-destructive ml-2">Ошибок: {wave.result.failed}</span>
                  )}
                  {wave.result.errors.length > 0 && (
                    <div className="mt-1 text-destructive space-y-0.5">
                      {wave.result.errors.slice(0, 5).map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                      {wave.result.errors.length > 5 && (
                        <p>...и ещё {wave.result.errors.length - 5} ошибок</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Отмена</Button>
        {isRunning && onStop && (
          <Button variant="outline" onClick={onStop}>
            Остановить
          </Button>
        )}
      </div>
    </div>
  );
}
